import { useTabletOrMobile } from '@/hooks/useMediaQuery';
import React, { useContext, useEffect, useMemo } from 'react';
import { useGetInvoiceCommonData } from '@/hooks/useGetInvoice';
import MobileLayout from './Mobile';
import DesktopLayout from './Desktop';
import { StoreContext } from '@/providers/storeProvider';
import { airTracker } from '@/utils/airTracker';
import { COMPONENT_NAME } from '@/pages/Main';
import { Formik } from 'formik';
import { getCustomerDisplayName } from '@/utils/customer';
import { validateCustomerForm } from '@/utils/validate';
import useQuery from '@/hooks/useQuery';
import { CollectionMethod } from '@/constants';
import { InvoiceDetail } from '@/components/Invoice/types';

export type CustomerInfoForm = {
  name?: string;
  email?: string;
};
interface IGetLayoutProps {
  enabledPA: boolean;
  isPaid: boolean;
  autoCharge: boolean;
  isVoid: boolean;
  isBankTransfer: boolean;
  isReturningFromBankTransferPage: boolean;
}
export enum PageLayout {
  ONE_PAGE = 1,
  TWO_PAGE = 2,
}
const getLayoutProps = ({
  enabledPA,
  isPaid,
  autoCharge,
  isVoid,
  isBankTransfer,
  isReturningFromBankTransferPage,
}: IGetLayoutProps) => {
  // A3 A4 A5 A6, B3 B7 B8 B9 B10 / B4 B5
  if (!enabledPA || isPaid || isVoid || autoCharge) return PageLayout.ONE_PAGE;
  // Bank transfer drop-in return — show one-page pending layout
  if (isBankTransfer && isReturningFromBankTransferPage) return PageLayout.ONE_PAGE;
  // A1 A2, B1 B2 B6
  return PageLayout.TWO_PAGE;
};

interface IGetBannerTypeProps {
  isPaid: boolean;
  overdue: boolean;
  isSubscription: boolean;
  autoCharge: boolean;
  isInDunning: boolean;
  isDunningFailed: boolean;
  isVoid: boolean;
  isBankTransfer: boolean;
  isReturningFromBankTransferPage: boolean;
  collectionMethod: string;
}

export enum BannerType {
  NONE = 'NONE',
  PAID = 'PAID',
  PENDING_DROP_IN = 'PENDING_DROP_IN',
  OVERDUE = 'OVERDUE',
  SCHEDULED = 'SCHEDULED',
  VOID = 'VOID',
}

const getBannerType = ({
  isPaid,
  overdue,
  isSubscription,
  autoCharge,
  isInDunning,
  isDunningFailed,
  isVoid,
  isBankTransfer,
  isReturningFromBankTransferPage,
  collectionMethod,
}: IGetBannerTypeProps) => {
  if (isVoid) return BannerType.VOID;
  if (isPaid) return BannerType.PAID;
  // A2 A5 B2 B9 B5 B6
  if (overdue) return BannerType.OVERDUE;
  // B4
  if (isSubscription && autoCharge && !isInDunning && !isDunningFailed) return BannerType.SCHEDULED;
  // TEMPORARY FEATURE: bank transfer drop-in pending — this entire flow is a temporary workaround
  // shown when the payer returns from hosted checkout with a bank transfer ga_order_id.
  // Remove when billing exposes reliable server-side payment-finalization state.
  if (isBankTransfer && isReturningFromBankTransferPage && collectionMethod === CollectionMethod.CHARGE_ON_CHECKOUT)
    return BannerType.PENDING_DROP_IN;
  return BannerType.NONE;
};

type LayoutProps = {
  onRendered?: () => void;
};

/** Returns true when the `?return=true` query parameter is present.
 *
 * This is a temporary workaround to handle the post-drop-in hosted invoice UX.
 * This parameter is defined in the backend to indicate that the invoice is being returned from the bank transfer drop-in flow.
 */
const isReturnTrueParam = (returnParam: string | string[] | null | undefined) => {
  if (returnParam == null) return false;
  const values = (Array.isArray(returnParam) ? returnParam : [returnParam]).map((v) => String(v).trim().toLowerCase());
  return values.some((v) => v === 'true');
};

/** Returns true when the invoice has a non-empty bank transfer `ga_order_id`. */
const isBankTransfer = (inv: InvoiceDetail) => {
  const id = inv.paymentMethodInfo?.billingPayment?.bankTransfer?.gaOrderId;
  return String(id ?? '').trim() !== '';
};

const Layout = ({ onRendered }: LayoutProps) => {
  const isTabletOrMobile = useTabletOrMobile();
  const { invoiceData } = useContext(StoreContext);
  const { invoice, customerSnapshot } = invoiceData;
  const { isSubscription, overdue, autoCharge, enabledPA, isPaid, isInDunning, isDunningFailed, isVoid } =
    useGetInvoiceCommonData(invoice);
  const query = useQuery<{ return?: string | string[] }>();
  const isReturningFromBankTransferPage = useMemo(() => isReturnTrueParam(query.return), [query.return]);
  const isBankTransferInvoice = useMemo(() => isBankTransfer(invoice), [invoice]);
  const customerDisplayName = getCustomerDisplayName(customerSnapshot);
  const customerEmail = customerSnapshot?.email;
  const layout = getLayoutProps({
    enabledPA,
    isPaid,
    autoCharge,
    isVoid,
    isBankTransfer: isBankTransferInvoice,
    isReturningFromBankTransferPage,
  });
  const bannerType = getBannerType({
    isPaid,
    overdue,
    isSubscription,
    autoCharge,
    isInDunning,
    isDunningFailed,
    isVoid,
    isBankTransfer: isBankTransferInvoice,
    isReturningFromBankTransferPage,
    collectionMethod: invoice.collectionMethod,
  });

  useEffect(() => {
    airTracker.info('layout', {
      component: COMPONENT_NAME,
      isSubscription,
      overdue,
      autoCharge,
      enabledPA,
      isPaid,
      isInDunning,
      isDunningFailed,
      layout,
      bannerType,
      invoiceData,
      isTabletOrMobile,
    });
    onRendered?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onRendered]);

  return (
    <Formik<CustomerInfoForm>
      initialValues={{
        name: customerDisplayName || '',
        email: customerEmail || '',
      }}
      validateOnChange={false}
      validateOnBlur={false}
      validate={(values) => validateCustomerForm(values)}
      onSubmit={() => {
        console.log('submit');
      }}
    >
      {isTabletOrMobile ? (
        <MobileLayout layout={layout} bannerType={bannerType} />
      ) : (
        <DesktopLayout layout={layout} bannerType={bannerType} />
      )}
    </Formik>
  );
};

export default Layout;
